import { supabase } from '@/lib/supabaseClient';

/**
 * Normalize base name - NO MODIFICATIONS
 * Passes the full name as-is to the RPC (just trimmed)
 *
 * Client does NOT strip trailing numbers - RPC handles everything!
 *
 * @param name - Input name (e.g., "MACD", "MACD 3", "Alpha")
 * @returns Same name, just trimmed
 *
 * @example
 * normalizeBaseName("MACD")        // "MACD"
 * normalizeBaseName("MACD 3")      // "MACD 3"
 * normalizeBaseName("  Alpha  ")   // "Alpha"
 */
export function normalizeBaseName(name: string): string {
  // ✅ Just trim - NO modifications
  return name.trim();
}

/**
 * Get next available backtest title for user (GLOBAL - considers all backtests)
 *
 * @deprecated Use nextBacktestTitleForBot instead for per-bot numbering
 *
 * CRITICAL: This function ONLY uses DB RPC. NO client-side modifications.
 * Client passes base name as-is, RPC does ALL the work.
 *
 * Flow:
 * 1. Trim input (NO stripping of numbers)
 * 2. Call RPC with p_base_name and p_user_id
 * 3. RPC returns next title (e.g., "MACD", "MACD 1", "MACD 2")
 * 4. Client uses returned title EXACTLY as-is for insert
 *
 * Base name rules:
 * - From My Bots → baseName = bot.name (e.g., "MACD")
 * - From Backtests Edit → baseName = backtest.title (e.g., "MACD 3")
 *
 * @param baseName - Base name (e.g., "MACD" or "MACD 3")
 * @param userId - User ID to scope the query
 * @returns Next backtest title from RPC (use as-is!)
 * @throws Error if RPC fails or returns invalid data
 *
 * @example
 * // Bot "MACD", no backtests yet
 * await nextBacktestTitleForUser("MACD", userId) // "MACD"
 *
 * @example
 * // Bot "MACD", has ["MACD"]
 * await nextBacktestTitleForUser("MACD", userId) // "MACD 1"
 *
 * @example
 * // Bot "MACD 3", has ["MACD 3"]
 * await nextBacktestTitleForUser("MACD 3", userId) // "MACD 3 1"
 */
export async function nextBacktestTitleForUser(baseName: string, userId: string): Promise<string> {
  const base = normalizeBaseName(baseName);

  const { data, error } = await supabase.rpc('f_next_backtest_title', {
    p_base_name: base,     // EXACTLY these key names
    p_user_id: userId,
  });

  if (error) {
    console.error('[nextBacktestTitleForUser] RPC error:', error);
    throw new Error(`Failed to generate backtest title: ${error.message}`);
  }

  if (!data || typeof data !== 'string') {
    throw new Error('RPC returned invalid title');
  }

  console.log(`[nextBacktestTitleForUser] base=${base} -> title=${data}`);
  return data;
}

/**
 * ✅ Get next available backtest title FOR THIS BOT ONLY (per-bot numbering)
 *
 * Uses "smallest unused index" logic:
 * - base → index 0 (no suffix)
 * - "base 1" → index 1
 * - "base 2" → index 2
 *
 * Finds the smallest unused index and returns corresponding title.
 *
 * Only considers backtests for the SAME bot_id (not other bots!)
 *
 * @param baseName - Base name (typically bot.name, e.g., "MACD")
 * @param userId - User ID
 * @param botId - Bot ID to scope the query
 * @returns Next backtest title (e.g., "MACD", "MACD 1", "MACD 2")
 *
 * @example
 * // Bot "MACD" (bot_id=abc), no backtests yet
 * await nextBacktestTitleForBot("MACD", userId, "abc") // "MACD" (index 0)
 *
 * @example
 * // Bot "MACD" (bot_id=abc), has ["MACD"]
 * await nextBacktestTitleForBot("MACD", userId, "abc") // "MACD 1" (index 1)
 *
 * @example
 * // Bot "MACD" (bot_id=abc), has ["MACD", "MACD 2"] (deleted "MACD 1")
 * await nextBacktestTitleForBot("MACD", userId, "abc") // "MACD 1" (smallest unused)
 */
export async function nextBacktestTitleForBot(
  baseName: string,
  userId: string,
  botId: string
): Promise<string> {
  const base = normalizeBaseName(baseName);

  // ✅ Fetch existing backtest titles FOR THIS BOT ONLY
  const { data: rows, error: fetchError } = await supabase
    .from('backtest_results')
    .select('bot_name')
    .eq('user_id', userId)
    .eq('bot_id', botId);

  if (fetchError) {
    console.error('[nextBacktestTitleForBot] Fetch error:', fetchError);
    throw new Error(`Failed to fetch backtest titles: ${fetchError.message}`);
  }

  const existingTitles = (rows || []).map(r => r.bot_name || '');

  // ✅ Parse existing titles to find used indices
  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`^${esc(base)}(?:\\s+(\\d+))?$`, 'i');

  const used = new Set<number>();
  for (const t of existingTitles) {
    const m = t.match(re);
    if (!m) continue;
    used.add(m[1] ? parseInt(m[1], 10) : 0); // base → 0; "base N" → N
  }

  // ✅ Find smallest unused index
  let n = 0;
  while (used.has(n)) {
    n++;
  }

  // ✅ Format title: 0 → base, 1+ → "base N"
  const title = n === 0 ? base : `${base} ${n}`;
  console.log(`[nextBacktestTitleForBot] base=${base}, used=${Array.from(used).sort((a,b)=>a-b).join(',')}, next=${n} -> title=${title}`);
  return title;
}

/**
 * Get next available bot name for user (NO timestamps, NO fallbacks)
 *
 * Similar to nextBacktestTitleForUser but for bots.
 * If no active bot named "MACD" exists, returns "MACD"
 * Otherwise returns "MACD 2", "MACD 3", etc.
 *
 * @param baseName - Preferred base name (e.g., "MACD 16" or "My Bot")
 * @param userId - User ID to scope the query
 * @returns Unique bot name (e.g., "MACD", "MACD 2", "MACD 3")
 * @throws Error if RPC fails or returns invalid data
 */
export async function nextBotNameForUser(baseName: string, userId: string): Promise<string> {
  const base = normalizeBaseName(baseName);

  console.log('[nextBotNameForUser] Input:', {
    originalBaseName: baseName,
    normalizedBase: base,
    userId,
    userIdType: typeof userId
  });

  // Validate UUID format
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(userId)) {
    console.error('[nextBotNameForUser] Invalid UUID format:', userId);
    throw new Error(`Invalid user ID format: ${userId}`);
  }

  console.log('[nextBotNameForUser] Calling RPC f_next_bot_name with:', {
    p_base_name: base,
    p_user_id: userId
  });

  const { data, error } = await supabase.rpc('f_next_bot_name', {
    p_base_name: base,     // EXACTLY these key names
    p_user_id: userId,
  });

  console.log('[nextBotNameForUser] RPC response:', {
    data,
    error,
    dataType: typeof data
  });

  if (error) {
    console.error('[nextBotNameForUser] RPC error details:', {
      message: error.message,
      details: error.details,
      hint: error.hint,
      code: error.code
    });
    throw new Error(`Failed to generate unique bot name: ${error.message}`);
  }

  if (!data || typeof data !== 'string') {
    console.error('[nextBotNameForUser] Invalid response data:', data);
    throw new Error('RPC returned invalid bot name');
  }

  console.log(`[nextBotNameForUser] base=${base} -> name=${data}`);
  return data;
}
